import { type ReactElement, useState } from 'react';

import { c } from 'ttag';

import { Button } from '@proton/atoms/Button/Button';
import { IcPlus } from '@proton/icons/icons/IcPlus';
import type { Plan } from '@proton/payments/core/plan/interface';
import type { PaymentTelemetryContext } from '@proton/payments/telemetry/helpers';
import { checkoutTelemetry } from '@proton/payments/telemetry/telemetry';
import { BRAND_NAME, LUMO_APP_NAME } from '@proton/shared/lib/constants';

import useSubscriptionModalTelemetry from '../subscription/useSubscriptionModalTelemetry';
import { NumberCustomiser, type NumberCustomiserProps } from './NumberCustomiser';

const LumoAddonBanner = ({ onClick, price }: { onClick: () => void; price: ReactElement }) => (
    <div
        className="border p-4 flex flex-column lg:flex-row gap-2 flex-nowrap items-start lg:items-center rounded-lg"
        data-testid="lumo-addon-banner"
    >
        <div className="w-full">
            <p className="m-0 mb-1 text-lg">
                <strong className="block lg:inline">{c('collider_2025: Info').t`Add ${LUMO_APP_NAME}`}</strong>{' '}
                {c('mail_signup_2024: Info').jt`for ${price}`}
            </p>
            <p className="m-0 text-sm color-weak">
                {c('collider_2025: Info')
                    .t`${LUMO_APP_NAME} is a privacy-first AI that helps you get more done while keeping your data secure. Unlock its full potential and enjoy early access to new features as they’re introduced.`}
            </p>
        </div>
        <Button color="norm" shape="outline" className="shrink-0 flex items-center gap-1" pill onClick={onClick}>
            <IcPlus className="shrink-0" />
            <span data-testid="lumo-addon-banner-add-button">{c('Action').t`Add`}</span>
        </Button>
    </div>
);

interface LumoAddonProps extends Omit<NumberCustomiserProps, 'label' | 'tooltip'> {
    price: ReactElement;
    addon: Plan;
    onAddLumo: () => void;
    telemetryContext: PaymentTelemetryContext;
}

const LumoAddon = ({ price, onAddLumo, value, telemetryContext, ...rest }: LumoAddonProps) => {
    const { reportAddLumo } = useSubscriptionModalTelemetry();

    const [showLumoBanner, setShowLumoBanner] = useState(value === 0);

    if (showLumoBanner) {
        return (
            <div>
                <LumoAddonBanner
                    price={price}
                    onClick={() => {
                        setShowLumoBanner(false);
                        onAddLumo();
                        void reportAddLumo();
                        checkoutTelemetry.reportAddLumo({ context: telemetryContext });
                    }}
                />
            </div>
        );
    }

    return (
        <NumberCustomiser
            label={LUMO_APP_NAME}
            value={value}
            tooltip={c('Info').t`${LUMO_APP_NAME} includes ${BRAND_NAME} Scribe writing assistant`}
            {...rest}
        />
    );
};

export default LumoAddon;
